import React from 'react';
import { Link } from 'react-router-dom';
import { ClipboardCheck, Plus, Loader2, ChevronRight, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { vetassess, matters as mattersApi } from '@/lib/api';

const STAGE_COLORS = {
  DRAFT: 'bg-slate-100 text-slate-700',
  DOCUMENTS_COLLECTED: 'bg-blue-100 text-blue-700',
  FEE_PAID: 'bg-indigo-100 text-indigo-700',
  SUBMITTED: 'bg-amber-100 text-amber-700',
  UNDER_ASSESSMENT: 'bg-purple-100 text-purple-700',
  AWAITING_INFO: 'bg-orange-100 text-orange-700',
  ASSESSED_POSITIVE: 'bg-emerald-100 text-emerald-700',
  ASSESSED_NEGATIVE: 'bg-rose-100 text-rose-700',
  WITHDRAWN: 'bg-slate-100 text-slate-500',
};

export default function VetassessListPage() {
  const [rows, setRows] = React.useState([]);
  const [ms, setMs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(() => {
    setLoading(true);
    Promise.all([vetassess.list(), mattersApi.list()])
      .then(([r, m]) => { setRows(r); setMs(m); })
      .finally(() => setLoading(false));
  }, []);
  React.useEffect(load, [load]);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#2ECC9A]/10">
            <ClipboardCheck className="h-5 w-5 text-[#2ECC9A]" />
          </div>
          <div>
            <h1 className="font-serif text-3xl font-bold tracking-tight text-[#1A3A5C]">VETASSESS Tracker</h1>
            <div className="text-sm text-slate-500">Full-cycle skills assessment tracking — checklist, stages, expected outcome.</div>
          </div>
        </div>
        <NewSubmissionDialog matters={ms} onCreated={load} />
      </div>

      <div className="mt-6 grid gap-3 md:grid-cols-4">
        {[
          { l: 'Active submissions', v: rows.filter(r => !['ASSESSED_POSITIVE','ASSESSED_NEGATIVE','WITHDRAWN'].includes(r.stage)).length },
          { l: 'Positive outcomes (12mo)', v: rows.filter(r => r.stage === 'ASSESSED_POSITIVE').length },
          { l: 'Awaiting info', v: rows.filter(r => r.stage === 'AWAITING_INFO').length },
          { l: 'Draft — not yet submitted', v: rows.filter(r => r.stage === 'DRAFT').length },
        ].map((s) => (
          <Card key={s.l} className="border-slate-200"><CardContent className="p-4">
            <div className="text-xs uppercase tracking-widest text-slate-500">{s.l}</div>
            <div className="mt-1 font-serif text-2xl font-bold text-[#1A3A5C]">{s.v}</div>
          </CardContent></Card>
        ))}
      </div>

      <div className="mt-6 overflow-hidden rounded-lg border border-slate-200 bg-white">
        {loading ? (
          <div className="flex items-center justify-center p-12"><Loader2 className="h-6 w-6 animate-spin text-[#2ECC9A]" /></div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center">
            <ClipboardCheck className="mx-auto h-8 w-8 text-slate-300" />
            <div className="mt-3 text-sm text-slate-500">No VETASSESS submissions yet. Start one from a matter above.</div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Applicant</th>
                <th className="px-4 py-3 text-left">Occupation</th>
                <th className="px-4 py-3 text-left">Matter</th>
                <th className="px-4 py-3 text-left">VET Ref</th>
                <th className="px-4 py-3 text-left">Stage</th>
                <th className="px-4 py-3 text-left">Docs</th>
                <th className="px-4 py-3 text-left">Expected by</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.id} className="transition-colors hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <Link to={`/app/vetassess/${r.id}`} className="font-medium text-[#1A3A5C] hover:text-[#2ECC9A]">{r.applicant_name}</Link>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <div className="font-medium text-[#1A3A5C]">{r.occupation}</div>
                    <div className="text-slate-500">ANZSCO {r.anzsco_code}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-600">{r.matter_ref}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-600">{r.reference_number || '—'}</td>
                  <td className="px-4 py-3"><Badge className={(STAGE_COLORS[r.stage] || 'bg-slate-100') + ' text-[10px] uppercase tracking-wider'}>{r.stage.replace(/_/g,' ')}</Badge></td>
                  <td className="px-4 py-3 text-xs">
                    <span className="font-medium text-[#1A3A5C]">{r.checklist.filter(c=>c.done).length}</span>/{r.checklist.length}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600"><Clock className="mr-1 inline h-3 w-3" />{r.expected_outcome_by}</td>
                  <td className="pr-4"><Link to={`/app/vetassess/${r.id}`}><ChevronRight className="h-4 w-4 text-slate-300" /></Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function NewSubmissionDialog({ matters, onCreated }) {
  const [open, setOpen] = React.useState(false);
  const [f, setF] = React.useState({ matter_id: '', applicant_name: '', occupation: '', anzsco_code: '', application_type: 'Full Skills Assessment' });
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState('');

  const pickMatter = (id) => {
    const m = matters.find((x) => x.id === id);
    setF({ ...f, matter_id: id, applicant_name: m?.applicant || '', occupation: m?.occupation || '' });
  };

  const submit = async (e) => {
    e.preventDefault(); setErr(''); setSaving(true);
    try { await vetassess.create(f); setOpen(false); onCreated?.(); setF({ matter_id: '', applicant_name: '', occupation: '', anzsco_code: '', application_type: 'Full Skills Assessment' }); }
    catch (er) { setErr(er?.response?.data?.detail || 'Create failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="h-10 gap-2 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90"><Plus className="h-4 w-4" /> New VETASSESS submission</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Start a VETASSESS submission</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>From matter</Label>
            <Select value={f.matter_id} onValueChange={pickMatter}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Choose a matter…" /></SelectTrigger>
              <SelectContent>{matters.map((m) => <SelectItem key={m.id} value={m.id}>{m.ref} — {m.applicant}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Applicant name</Label><Input required value={f.applicant_name} onChange={(e)=>setF({...f, applicant_name: e.target.value})} className="mt-1" /></div>
            <div><Label>Occupation</Label><Input required value={f.occupation} onChange={(e)=>setF({...f, occupation: e.target.value})} className="mt-1" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>ANZSCO code</Label><Input required value={f.anzsco_code} onChange={(e)=>setF({...f, anzsco_code: e.target.value})} placeholder="e.g. 225113" className="mt-1" /></div>
            <div><Label>Application type</Label>
              <Select value={f.application_type} onValueChange={(v)=>setF({...f, application_type: v})}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['Full Skills Assessment','Points Test Advice','Trade Skills Assessment','PYP'].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {err && <div className="rounded-md bg-red-50 p-2 text-xs text-red-700">{err}</div>}
          <DialogFooter>
            <Button type="submit" disabled={saving || !f.matter_id} className="bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90">{saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}Create submission</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
